import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Users, Shield, UserX, Search, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from '../../lib/api';

const PAGE_SIZE = 10;

export default function UsersPage() {
  const api = useApi();
  const [users, setUsers]       = useState([]);
  const [me, setMe]             = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab]     = useState('all');
  const [confirmModal, setConfirmModal] = useState(null);
  const [actionBusy, setActionBusy]   = useState(false);
  const [actionError, setActionError] = useState(null);
  const [page, setPage]               = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ur, mr] = await Promise.all([api.adminListUsers(), api.me()]);
      setUsers(ur.users || []);
      setMe(mr);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => ({
    total:       users.length,
    active:      users.filter(u => u.is_active === 1).length,
    deactivated: users.filter(u => u.is_active === 0).length,
    admins:      users.filter(u => u.is_platform_admin === 1).length,
  }), [users]);

  const filteredUsers = useMemo(() => users.filter(u => {
    if (activeTab === 'active'      && u.is_active !== 1)          return false;
    if (activeTab === 'deactivated' && u.is_active !== 0)          return false;
    if (activeTab === 'admins'      && u.is_platform_admin !== 1)  return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      return name.includes(q) || (u.email || '').toLowerCase().includes(q) || String(u.id).includes(q);
    }
    return true;
  }), [users, activeTab, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paged = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageNums = buildPageNums(page, totalPages);

  const executeAction = async () => {
    if (!confirmModal) return;
    setActionBusy(true); setActionError(null);
    try {
      if (confirmModal.type === 'deactivate' || confirmModal.type === 'activate')
        await api.adminToggleUserActive(confirmModal.user.id);
      else
        await api.adminToggleUserAdmin(confirmModal.user.id);
      await loadData();
      setConfirmModal(null);
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message);
    } finally {
      setActionBusy(false);
    }
  };

  const getInitials = (u) => {
    const f = u.first_name?.[0] || '';
    const l = u.last_name?.[0]  || '';
    return (f + l).toUpperCase() || (u.email?.[0] || '?').toUpperCase();
  };

  const userName = (u) =>
    (u.first_name || u.last_name) ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.email || 'Unnamed';

  const TABS = [
    { key: 'all',         label: `All (${stats.total})` },
    { key: 'active',      label: `Active (${stats.active})` },
    { key: 'deactivated', label: `Deactivated (${stats.deactivated})` },
    { key: 'admins',      label: `Admins (${stats.admins})` },
  ];

  return (
    <>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Platform Users</h1>
          <p className="admin-page-subtitle">Manage all platform accounts, deactivation status, and administration privileges.</p>
        </div>
        <div className="admin-header-actions">
          <button className="btn-secondary" onClick={loadData} disabled={loading} style={{ gap: 6 }}>
            <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="admin-banner error">{error}</div>}

      {/* KPI tiles */}
      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon blue"><Users size={18} /></div>
          <p className="admin-kpi-label">Total Platform Users</p>
          <p className="admin-kpi-value">{stats.total}</p>
          <p className="admin-kpi-sub">Registered accounts</p>
        </div>
        <div className="admin-kpi ok">
          <div className="admin-kpi-icon green"><Users size={18} /></div>
          <p className="admin-kpi-label">Active Users</p>
          <p className="admin-kpi-value">{stats.active}</p>
          <p className="admin-kpi-sub">Can access workspaces</p>
        </div>
        <div className={`admin-kpi ${stats.deactivated > 0 ? 'danger' : ''}`}>
          <div className={`admin-kpi-icon ${stats.deactivated > 0 ? 'red' : 'purple'}`}><UserX size={18} /></div>
          <p className="admin-kpi-label">Deactivated</p>
          <p className="admin-kpi-value">{stats.deactivated}</p>
          <p className="admin-kpi-sub">Blocked from logging in</p>
        </div>
        <div className="admin-kpi warn">
          <div className="admin-kpi-icon orange"><Shield size={18} /></div>
          <p className="admin-kpi-label">Platform Admins</p>
          <p className="admin-kpi-value">{stats.admins}</p>
          <p className="admin-kpi-sub">Full system privilege</p>
        </div>
      </div>

      {/* Table card */}
      <div className="admin-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
          {/* Pill tabs */}
          <div style={{ display: 'flex', background: '#f3f4f6', padding: 3, borderRadius: 10, gap: 2 }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => { setActiveTab(t.key); setPage(1); }} style={{
                font: 'inherit', fontSize: 13, fontWeight: 600, padding: '5px 12px', borderRadius: 8,
                border: 'none', cursor: 'pointer',
                background: activeTab === t.key ? 'white' : 'transparent',
                color: activeTab === t.key ? '#0f1c2e' : '#6b7280',
                boxShadow: activeTab === t.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{t.label}</button>
            ))}
          </div>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
            <input
              placeholder="Search by name, email or ID..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              autoComplete="off"
              style={{ font: 'inherit', fontSize: 13, padding: '6px 12px 6px 30px', border: '1px solid #d1d5db', borderRadius: 8, width: 240, outline: 'none', color: '#374151' }}
            />
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="admin-empty">No users match.</div>
        ) : (
          <div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>USER</th>
                <th>EMAIL</th>
                <th>ROLE</th>
                <th style={{ textAlign: 'center' }}>WORKSPACES</th>
                <th>STATUS</th>
                <th>JOINED</th>
                <th style={{ textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => {
                const isSelf = me && u.id === me.id;
                const isAdmin = u.is_platform_admin === 1;
                const isActive = u.is_active === 1;
                return (
                  <tr key={u.id} style={{ opacity: isActive ? 1 : 0.7 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                          background: isAdmin ? '#dbeafe' : '#f3f4f6',
                          color: isAdmin ? '#2563eb' : '#6b7280',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700,
                          border: isAdmin ? '1.5px solid #93c5fd' : '1.5px solid #e5e7eb',
                        }}>
                          {getInitials(u)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#0f1c2e', fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                            {userName(u)}
                            {isSelf && <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>(You)</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>#{u.id}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: '#374151' }}>{u.email}</td>
                    <td>
                      {isAdmin
                        ? <span className="admin-status-pill running">★ Admin</span>
                        : <span className="admin-status-pill skipped">Member</span>}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#374151' }}>{u.workspace_count}</td>
                    <td>
                      {isActive
                        ? <span className="admin-status-pill ok">Active</span>
                        : <span className="admin-status-pill failed">Deactivated</span>}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280' }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {isAdmin ? (
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 10px' }}
                            disabled={isSelf}
                            onClick={() => setConfirmModal({ type: 'demote', user: u })}>
                            Demote
                          </button>
                        ) : (
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 10px', color: '#2563eb', borderColor: '#bfdbfe' }}
                            disabled={isSelf || !isActive}
                            onClick={() => setConfirmModal({ type: 'promote', user: u })}>
                            Promote
                          </button>
                        )}
                        {isActive ? (
                          <button className="btn-danger" style={{ fontSize: 12, padding: '3px 10px' }}
                            disabled={isSelf}
                            onClick={() => setConfirmModal({ type: 'deactivate', user: u })}>
                            Deactivate
                          </button>
                        ) : (
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 10px', color: '#16a34a', borderColor: '#bbf7d0' }}
                            disabled={isSelf}
                            onClick={() => setConfirmModal({ type: 'activate', user: u })}>
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredUsers.length)} to {Math.min(page * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} users
            </span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page === 1)}>
                <ChevronLeft size={14} />
              </button>
              {pageNums.map((p, i) =>
                p === '…'
                  ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: 13 }}>…</span>
                  : <button key={p} onClick={() => setPage(p)} style={pageBtn(false, page === p)}>{p}</button>
              )}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page === totalPages)}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmModal && (
        <div className="modal-backdrop" onClick={() => !actionBusy && setConfirmModal(null)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h2>
                {confirmModal.type === 'deactivate' && 'Deactivate User'}
                {confirmModal.type === 'activate'   && 'Reactivate User'}
                {confirmModal.type === 'promote'    && 'Promote to Admin'}
                {confirmModal.type === 'demote'     && 'Demote Admin'}
              </h2>
              <button className="modal-close" onClick={() => setConfirmModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {actionError && <div className="admin-banner error">{actionError}</div>}
              <p style={{ color: '#374151', fontSize: 14, lineHeight: 1.6 }}>
                Are you sure you want to <strong>{confirmModal.type}</strong> <strong>{userName(confirmModal.user)}</strong>?
              </p>
              {confirmModal.type === 'deactivate' && (
                <p style={{ fontSize: 13, color: '#b42318', background: '#fef2f2', padding: '8px 12px', borderRadius: 8, marginTop: 8 }}>
                  The user will be blocked from logging in immediately.
                </p>
              )}
              {confirmModal.type === 'promote' && (
                <p style={{ fontSize: 13, color: '#b54708', background: '#fff7ed', padding: '8px 12px', borderRadius: 8, marginTop: 8 }}>
                  Platform admins have full access including billing, plans, and user management.
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <button className="btn-secondary" onClick={() => setConfirmModal(null)} disabled={actionBusy}>Cancel</button>
                <button
                  className={confirmModal.type === 'deactivate' || confirmModal.type === 'demote' ? 'btn-danger' : 'btn-primary'}
                  disabled={actionBusy}
                  onClick={executeAction}
                >
                  {actionBusy ? 'Processing…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </>
  );
}

function buildPageNums(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1, 2, 3];
  if (cur > 3 && cur < total - 1) { pages.push('…', cur); }
  pages.push('…', total);
  return [...new Set(pages)];
}

function pageBtn(disabled, active) {
  return {
    width: 32, height: 32, border: '1px solid #e5e7eb', borderRadius: 6,
    background: active ? '#0f1c2e' : 'white',
    color: disabled ? '#d1d5db' : active ? 'white' : '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
}
